import { useCallback, useEffect, useMemo, useState } from 'react';

import { useStudentSubscriptions } from '@/store/students';
import { useMutateUserSubscriptions } from '@/store/subscriptions';

type VisibilityMap = Record<string, boolean>;

export const useUserSubscriptionsVisibility = (studentId: string) => {
  const { subscriptions, isLoading } = useStudentSubscriptions(studentId);
  const [localSelected, setLocalSelected] = useState<VisibilityMap>({});
  const { setUserVisibleSubscriptions, isPending } = useMutateUserSubscriptions();

  useEffect(() => {
    if (!subscriptions) return;
    const initial: VisibilityMap = {};
    subscriptions.forEach(sub => {
      initial[sub._id] = !sub.isHidden;
    });
    setLocalSelected(initial);
  }, [subscriptions]);

  const onToggle = useCallback((subscriptionId: string) => {
    setLocalSelected(prev => ({
      ...prev,
      [subscriptionId]: !(prev && prev[subscriptionId])
    }));
  }, []);

  const selectedIds = useMemo(
    () =>
      (subscriptions || [])
        .filter(sub =>
          localSelected[sub._id] === undefined ? !sub.isHidden : localSelected[sub._id]
        )
        .map(sub => sub._id),
    [subscriptions, localSelected]
  );

  const hasChanges = useMemo(() => {
    if (!subscriptions) return false;
    return subscriptions.some(sub => {
      const initial = !sub.isHidden;
      const current = localSelected[sub._id] ?? initial;
      return current !== initial;
    });
  }, [subscriptions, localSelected]);

  const canSave = useMemo(() => hasChanges && selectedIds.length > 0, [hasChanges, selectedIds]);

  const saveVisibility = useCallback(async () => {
    if (!canSave) return;
    await setUserVisibleSubscriptions({ userId: studentId, subscriptionIds: selectedIds });
  }, [canSave, setUserVisibleSubscriptions, studentId, selectedIds]);

  return {
    subscriptions,
    isLoading,
    localSelected,
    onToggle,
    canSave,
    isSaving: isPending,
    saveVisibility
  };
};
